<script setup lang="ts">
import { getUiLanguage } from "@/shared/services/providers/language-provider";
import { useI18n } from "vue-i18n";
import { getCurrencySymbol } from "../helper/get-currency-symbol";
import { computed } from "vue";

// props
const props = withDefaults(
    defineProps<{
        price?: number | string | null;
        currency?: string;
        convertToDecimal?: boolean;
        useCurrencySymbol?: boolean;
        preLabel?: string;
        noPriceLabel?: string;
    }>(),
    {
        price: null,
        convertToDecimal: false,
        useCurrencySymbol: false,
    }
);

const { t } = useI18n();

const isValidPrice = computed(() => {
    return props.price !== null && props.price !== "" && props.currency !== undefined;
});

// format the price
function formatPrice(price: number | string | null): string {
    // show no price label if price is null
    if (price === null) {
        return props.noPriceLabel ?? t("shop.no price information");
    }
    // convert price to a number, if needed
    const numberedPrice = typeof price === "number" ? price : Number(price);
    // convert to decimal, if needed
    const priceWithDecimal = props.convertToDecimal ? numberedPrice / 100 : numberedPrice;
    // convert to locale string representation respectively
    return priceWithDecimal.toLocaleString(getUiLanguage(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 3,
    });
}
</script>
<template>
    <div v-if="isValidPrice" class="row items-end">
        <span class="pre-label" v-if="preLabel">{{ preLabel }}:</span>
        <div class="price-value row items-end">
            <div v-if="preLabel">&nbsp;</div>
            {{ formatPrice(price) }}
            <template v-if="currency && price !== null">
                {{ useCurrencySymbol ? getCurrencySymbol(currency) : currency }}
            </template>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.pre-label {
    font-size: 12px;
    white-space: pre;
    color: $grey-30;
    font-weight: normal;
}

.price-value {
    white-space: nowrap;
}
</style>
